export const Operators = {
  null: '=isnull=',
  notnull: '=isnull=',
  eq: '==',
  ne: '!=',
  gt: '=gt=',
  ge: '=ge=',
  lt: '=lt=',
  le: '=le=',
  in: '=in=',
  containsIgnoreCase: '=containsignorecase=',
  startsWithIgnoreCase: '=startswithignorecase=',
} as const;

export type Operator = keyof typeof Operators;

export function and(...args: readonly string[]) {
  return `(${args.join(';')})`;
}

export function or(...args: readonly string[]) {
  return `(${args.join(',')})`;
}

const rsqlFilter = (
  key: string,
  operation: (typeof Operators)[keyof typeof Operators],
  value: string | readonly string[] | boolean,
) => {
  if (Array.isArray(value)) {
    return `${key}${operation}(${value.map(escapeRsqlValue).join(',')})`;
  } else if (typeof value === 'boolean') {
    return `${key}${operation}${value}`;
  } else {
    return `${key}${operation}${escapeRsqlValue(String(value))}`;
  }
};

// Escape special characters in RSQL values to prevent injection attacks
const escapeRsqlValue = (value: string): string => {
  // Always escape and quote to prevent RSQL injection
  // This approach is more secure than selective escaping
  const escapedValue = value
    .replace(/\\/g, '\\\\') // Escape backslashes first
    .replace(/"/g, '\\"') // Escape double quotes
    .replace(/'/g, "\\'"); // Escape single quotes

  // Always wrap in quotes for consistent, safe handling
  // This prevents injection via RSQL operators (=, !, <, >, etc.)
  return `"${escapedValue}"`;
};

export const isNull = (key: string) => rsqlFilter(key, Operators.null, true);
export const isNotNull = (key: string) => rsqlFilter(key, Operators.notnull, false);

export const equals = (key: string, value: string) => rsqlFilter(key, Operators.eq, value);
export const notEquals = (key: string, value: string) => rsqlFilter(key, Operators.ne, value);
export const greaterThanOrEqualTo = (key: string, value: string) =>
  rsqlFilter(key, Operators.ge, value);
export const lessThanOrEqualTo = (key: string, value: string) =>
  rsqlFilter(key, Operators.le, value);
export const includes = (key: string, value: readonly string[]) =>
  rsqlFilter(key, Operators.in, value);
export const containsIgnoreCase = (key: string, value: string) =>
  rsqlFilter(key, Operators.containsIgnoreCase, value);
export const startsWithIgnoreCase = (key: string, value: string) =>
  rsqlFilter(key, Operators.startsWithIgnoreCase, value);
export const greaterThan = (key: string, value: string) => rsqlFilter(key, Operators.gt, value);
export const lessThan = (key: string, value: string) => rsqlFilter(key, Operators.lt, value);
